import axios from 'axios'
import React, { useEffect, useState } from 'react'
import toastr from 'toastr'

import CreditTransactionTabs from '../app/components/CreditTransactionTabs'
import Loading from '../app/components/Loading'
import ROUTES_ICBCVERIFICATION from '../app/routes/ICBCVerification'
import ROUTES_UPLOADS from '../app/routes/Uploads'
import CustomPropTypes from '../app/utilities/props'
import UploadVerificationData from './components/UploadVerificationData'

const UploadICBCVerificationContainer = (props) => {
  const [loading, setLoading] = useState(true)
  const { user } = props
  const [dateCurrentTo, setDateCurrentTo] = useState('')
  const [previousDateCurrentTo, setPreviousDateCurrentTo] = useState(
    'No ICBC data uploaded yet'
  )
  const [files, setFiles] = useState([])
  const [alertMessage, setAlertMessage] = useState(null)
  const [showProcessing, setShowProcessing] = useState(false)
  const [showProgressBar, setShowProgressBar] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [progressStatus, setProgressStatus] = useState('')
  const [pollingInterval, setPollingInterval] = useState(null)

  const today = new Date()
  const date = `${today.getFullYear()}-${
    today.getMonth() + 1
  }-${today.getDate()}`

  const refreshList = (showLoading) => {
    setLoading(showLoading)
    axios.get(ROUTES_ICBCVERIFICATION.DATE).then((response) => {
      if (response.data.uploadDate) {
        setPreviousDateCurrentTo(response.data.uploadDate)
      }
      setLoading(false)
    })
    setDateCurrentTo(date)
  }

  const updateProgressBar = (progressEvent) => {
    const percentage = Math.round(
      (100 * progressEvent.loaded) / progressEvent.total
    )
    setUploadProgress(percentage)
    setProgressStatus('Uploading file to storage...')
  }

  const pollProgress = (uploadId) => {
    // Store upload_id in localStorage for persistence across page refreshes
    localStorage.setItem('icbc_upload_id', uploadId)
    localStorage.setItem('icbc_upload_active', 'true')
    
    const interval = setInterval(() => {
      axios
        .get(ROUTES_ICBCVERIFICATION.PROGRESS, {
          params: { upload_id: uploadId }
        })
        .then((response) => {
          const { progress, status: statusText, complete, error, results } = response.data
          
          setUploadProgress(progress)
          setProgressStatus(statusText)
          
          if (complete) {
            clearInterval(interval)
            setPollingInterval(null)
            
            // Clear localStorage when complete
            localStorage.removeItem('icbc_upload_id')
            localStorage.removeItem('icbc_upload_active')
            
            if (error) {
              setAlertMessage(error)
              setShowProcessing(false)
              setShowProgressBar(false)
            } else if (results) {
              const { createdRecords, updatedRecords } = results
              setPreviousDateCurrentTo(results.dateCurrentTo)
              
              if (createdRecords === 0 && updatedRecords === 0) {
                setAlertMessage('upload successful - no new records were found.')
              } else {
                setAlertMessage('upload successful - ' +
                  createdRecords + ' new records were created and ' +
                  updatedRecords + ' records were updated.')
              }
              
              toastr.success('upload successful!', '', {
                positionClass: 'toast-bottom-right'
              })
              
              setShowProcessing(false)
              setShowProgressBar(false)
            }
            
            setFiles([])
          }
        })
        .catch((error) => {
          console.error('Polling error:', error)
          clearInterval(interval)
          setPollingInterval(null)
          
          // Clear localStorage on error
          localStorage.removeItem('icbc_upload_id')
          localStorage.removeItem('icbc_upload_active')
          
          setAlertMessage('An error occurred while checking upload progress.')
          setShowProcessing(false)
          setShowProgressBar(false)
        })
    }, 10000) // Poll every 10 seconds
    
    setPollingInterval(interval)
  }

  const doUpload = () => {
    setShowProgressBar(true)
    setUploadProgress(0)
    setProgressStatus('Preparing upload...')

    files.forEach((file) => {
      axios.get(ROUTES_UPLOADS.MINIO_URL).then((response) => {
        const { url: uploadUrl, minioObjectName: filename } = response.data

        axios
          .put(uploadUrl, file, {
            headers: {
              Authorization: null
            },
            onUploadProgress: (progressEvent) => {
              updateProgressBar(progressEvent)
            }
          })
          .then(() => {
            // File uploaded to storage, now start processing
            setShowProcessing(true)
            setUploadProgress(0)
            setProgressStatus('Starting data processing...')

            axios
              .post(ROUTES_ICBCVERIFICATION.UPLOAD, {
                filename,
                submission_current_date: dateCurrentTo
              })
              .then((postResponse) => {
                const { upload_id: uploadId } = postResponse.data
                // Start polling for progress
                pollProgress(uploadId)
              })
              .catch((error) => {
                console.error(error)
                const { response: errorResponse } = error
                if (errorResponse?.status === 400) {
                  setAlertMessage(errorResponse.data)
                } else {
                  setAlertMessage(
                    'An error has occurred while uploading. Please try again later.'
                  )
                }
                setShowProcessing(false)
                setShowProgressBar(false)
              })
          })
          .catch((error) => {
            console.error(error)
            setAlertMessage(
              'An error has occurred while uploading. Please try again later.'
            )
            setShowProcessing(false)
            setShowProgressBar(false)
          })
      })
    })
  }

  useEffect(() => {
    refreshList(true)
    
    // Check if there's an active upload from a previous session
    const activeUploadId = localStorage.getItem('icbc_upload_id')
    const isUploadActive = localStorage.getItem('icbc_upload_active') === 'true'
    
    if (activeUploadId && isUploadActive) {
      // Resume showing progress bar and processing status
      setShowProgressBar(true)
      setShowProcessing(true)
      setProgressStatus('Resuming upload monitoring...')
      
      // Resume polling for the active upload
      pollProgress(activeUploadId)
    }
    
    // Cleanup polling interval on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
    }
  }, [])

  if (loading) {
    return <Loading />
  }

  return [
    <CreditTransactionTabs active="icbc-update" key="tabs" user={user} />,
    <UploadVerificationData
      alertMessage={alertMessage}
      dateCurrentTo={dateCurrentTo}
      files={files}
      key="page"
      previousDateCurrentTo={previousDateCurrentTo}
      setDateCurrentTo={setDateCurrentTo}
      setUploadFiles={setFiles}
      showProcessing={showProcessing}
      showProgressBar={showProgressBar}
      title="Upload ICBC Registration Data"
      upload={doUpload}
      uploadProgress={uploadProgress}
      progressStatus={progressStatus}
      user={user}
    />
  ]
}

UploadICBCVerificationContainer.propTypes = {
  user: CustomPropTypes.user.isRequired
}

export default UploadICBCVerificationContainer
